/* Drukke Mama's — "Recent bekeken door jou".
   Houdt bekeken product-handles bij in localStorage (key 'dm-recent', max 12)
   en rendert de laatst bekeken producten client-side. Geen jQuery. */

class DmRecentlyViewed extends HTMLElement {
  static STORAGE_KEY = 'dm-recent';
  static STORAGE_MAX = 12;

  connectedCallback() {
    const current = this.dataset.currentHandle || '';
    let handles = this.readHandles();

    if (current) {
      handles = [current].concat(handles.filter((handle) => handle !== current));
      handles = handles.slice(0, DmRecentlyViewed.STORAGE_MAX);
      try {
        localStorage.setItem(DmRecentlyViewed.STORAGE_KEY, JSON.stringify(handles));
      } catch (error) {
        /* private mode: geen historie */
      }
    }

    const toShow = handles.filter((handle) => handle !== current).slice(0, parseInt(this.dataset.max, 10) || 10);
    if (!toShow.length) return;

    this.render(toShow);
  }

  readHandles() {
    try {
      const parsed = JSON.parse(localStorage.getItem(DmRecentlyViewed.STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string') : [];
    } catch (error) {
      return [];
    }
  }

  render(handles) {
    const track = this.querySelector('[data-dm-rv-track]');
    if (!track) return;

    Promise.allSettled(
      handles.map((handle) =>
        fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`).then((response) => {
          if (!response.ok) throw new Error(handle);
          return response.json();
        })
      )
    ).then((results) => {
      const products = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
      if (!products.length) return;

      products.forEach((product) => track.append(this.buildCard(product)));
      this.hidden = false;
      // dm-carousel de knop-status laten herberekenen nu er items staan
      window.dispatchEvent(new Event('resize'));
    });
  }

  buildCard(product) {
    // Div met aparte link i.p.v. één grote anchor: er staat nu ook een
    // koopknop op de kaart en een button in een <a> is ongeldig.
    const card = document.createElement('div');
    card.className = 'dm-rv-card';

    const link = document.createElement('a');
    link.className = 'dm-rv-card__link';
    link.href = product.url;
    card.append(link);

    const media = document.createElement('div');
    media.className = 'dm-rv-card__media';
    const image = product.featured_image || (product.images && product.images[0]);
    if (image) {
      const img = document.createElement('img');
      img.src = this.sizedImage(image, 368);
      img.alt = product.title;
      img.loading = 'lazy';
      img.width = 184;
      img.height = 184;
      media.append(img);
    }
    if (product.compare_at_price > product.price) {
      const badge = document.createElement('span');
      badge.className = 'dm-rv-card__badge';
      badge.textContent = `-${Math.round((1 - product.price / product.compare_at_price) * 100)}%`;
      media.append(badge);
    }
    link.append(media);

    const info = document.createElement('div');
    info.className = 'dm-rv-card__info';

    const title = document.createElement('p');
    title.className = 'dm-rv-card__title';
    title.textContent = product.title;
    info.append(title);

    const prices = document.createElement('div');
    prices.className = 'dm-rv-card__prices';
    prices.append(this.moneyNode(product.price, 'dm-rv-card__price'));
    if (product.compare_at_price > product.price) {
      const old = this.moneyNode(product.compare_at_price, 'dm-rv-card__old');
      const strike = document.createElement('s');
      strike.append(old);
      prices.append(strike);
    }
    info.append(prices);
    link.append(info);

    const actions = document.createElement('div');
    actions.className = 'dm-rv-card__actions';
    actions.append(this.buildBuyButton(product));
    card.append(actions);
    return card;
  }

  buildBuyButton(product) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'dm-rv-card__add';

    const availableVariants = (product.variants || []).filter((variant) => variant.available);
    if (!product.available || !availableVariants.length) {
      button.textContent = 'Uitverkocht';
      button.disabled = true;
      return button;
    }

    if (product.variants.length > 1) {
      // Meerdere varianten: keuze hoort op de productpagina thuis.
      button.textContent = 'Opties kiezen';
      button.addEventListener('click', () => window.location.assign(product.url));
      return button;
    }

    button.textContent = 'In winkelmandje';
    button.addEventListener('click', () => this.addToCart(availableVariants[0].id, button));
    return button;
  }

  addToCart(variantId, button) {
    button.disabled = true;
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');
    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }

    fetch(window.routes?.cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: formData,
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message);
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
        } else {
          window.location.assign(window.routes?.cart_url || '/cart');
        }
      })
      .catch(() => {
        window.location.assign(window.routes?.cart_url || '/cart');
      })
      .finally(() => {
        button.disabled = false;
      });
  }

  moneyNode(cents, extraClass) {
    // 3295 -> 32⁹⁵, zoals snippets/dm-money.liquid
    const span = document.createElement('span');
    span.className = `dm-price${extraClass ? ` ${extraClass}` : ''}`;
    span.append(String(Math.floor(cents / 100)));
    const sup = document.createElement('sup');
    sup.setAttribute('aria-hidden', 'true');
    sup.textContent = String(cents % 100).padStart(2, '0');
    span.append(sup);
    const sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = `,${String(cents % 100).padStart(2, '0')}`;
    span.append(sr);
    return span;
  }

  sizedImage(url, width) {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', width);
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }
}

if (!customElements.get('dm-recently-viewed')) customElements.define('dm-recently-viewed', DmRecentlyViewed);
